// Public, framework-independent report form. The site app uses hash routes.
const getSession = () => window.AHAuth?.getSession() ?? Promise.resolve(null);

type Category = 'underage_suspicion' | 'non_consensual' | 'harassment' |
  'copyright_infringement' | 'payment_issue' | 'technical_bug' | 'other_policy_violation';

const trigger = document.getElementById('report-trigger') as HTMLButtonElement;
const modal = document.getElementById('report-modal') as HTMLDialogElement;
const form = document.getElementById('report-form') as HTMLFormElement;
const category = document.getElementById('report-category') as HTMLSelectElement;
const details = document.getElementById('report-details') as HTMLTextAreaElement;
const target = document.getElementById('report-target-url') as HTMLInputElement;
const email = document.getElementById('report-contact-email') as HTMLInputElement;
const emailField = document.getElementById('report-email-field') as HTMLElement;
const context = document.getElementById('report-video-context') as HTMLElement;
const error = document.getElementById('report-error') as HTMLElement;
const toast = document.getElementById('report-toast') as HTMLElement;
const submit = form.querySelector('button[type="submit"]') as HTMLButtonElement;

const clean = (value: string, max: number) => value.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, max);
const videoPath = () => {
  const path = (location.hash.startsWith('#/') ? location.hash.slice(1) : location.pathname);
  return path.match(/^\/watch\/([^/?#]+)/)?.[1] ?? null;
};
const videoTitle = () => clean(document.querySelector('.watch-page h1, .video-page h1, main h1')?.textContent ?? '', 300);
// Auth callbacks can leave tokens in a URL fragment; never submit those as page context.
const pageContextUrl = () => `${location.origin}${location.pathname}${location.hash.startsWith('#/') ? location.hash.split('?')[0] : ''}`;

async function open() {
  form.reset();
  error.textContent = '';
  const video = videoPath();
  const title = video ? videoTitle() : '';
  context.textContent = video ? `Current video: ${title || video}` : '';
  context.hidden = !video;
  emailField.hidden = false;
  modal.showModal();
  category.focus();
  try {
    emailField.hidden = Boolean(await getSession());
  } catch {
    emailField.hidden = false;
  }
}

function showToast(message: string) {
  toast.textContent = message;
  toast.hidden = false;
  window.setTimeout(() => { toast.hidden = true; }, 8000);
}

trigger.addEventListener('click', () => { void open(); });
category.addEventListener('change', () => {
  category.classList.toggle('report-urgent', category.value === 'underage_suspicion');
});
modal.querySelector('[data-report-close]')?.addEventListener('click', () => modal.close());
modal.addEventListener('click', (event) => {
  if (event.target === modal) modal.close();
});
form.addEventListener('submit', async (event) => {
  event.preventDefault();
  if (!form.reportValidity()) return;
  const reportDetails = clean(details.value, 5000);
  if (reportDetails.length < 10) {
    error.textContent = 'Please describe the issue in at least 10 characters.';
    details.focus();
    return;
  }
  const video = videoPath();
  const currentVideoId = video ? clean(video, 160) : null;
  const targetUrl = clean(target.value, 2048);
  const contactEmail = clean(email.value, 254).toLowerCase();
  const payload = {
    category: category.value as Category,
    details: reportDetails,
    target_url: targetUrl || pageContextUrl(),
    video_id: currentVideoId,
    video_title: video ? videoTitle() || null : null,
    contact_email: emailField.hidden ? null : contactEmail || null,
  };
  submit.disabled = true;
  error.textContent = '';
  try {
    const session = await getSession().catch(() => null);
    const response = await fetch('/api/reports/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
    const result = await response.json() as { id?: string; error?: string };
    if (!response.ok || !result.id) throw new Error(result.error || 'Unable to submit your report.');
    modal.close();
    showToast(`Report submitted to safety staff. Reference ID: #${result.id}`);
  } catch (cause) {
    error.textContent = cause instanceof Error ? cause.message : 'Unable to submit your report.';
  } finally {
    submit.disabled = false;
  }
});
